import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Star } from '../../../../UiKit/Star/Star';
import Spinner from 'sushiweb/Spinner/Spinner';
import { useMutationCall, useQueryCall } from '../../../../api/react-client';
import { useClient } from '../../../../api/api-client';
import { ERRORS, FIELDS, TEXT } from './constants';
import toast from 'react-hot-toast';

const ADONP_ENDPOINT = 'admin/adonp_dual_debit';

const AdonpDashboard = () => {
  const client = useClient();
  const form = useForm();
  const {
    register,
    handleSubmit,
    formState,
    watch,
    setValue,
    reset,
    setError,
  } = form;
  const { errors } = formState;
  const [isDetailsFetched, setIsDetailsFetched] = useState(false);
  const [orgId, setOrgId] = useState('');

  // fetch cart details
  const { isLoading: isFetchingCartDetails, refetch: getCartDetails } =
    useQueryCall({ enabled: false }, null, ['adonp_refund'], () =>
      client(
        `${ADONP_ENDPOINT}?cart_id=${watch('cartId')}&user_id=${watch(
          'userId',
        )}`,
      ).then((data) => {
        if (!data) return;
        Object.entries(FIELDS).forEach(([key, value]) => {
          setValue(key, data[value] ?? '');
        });
        setOrgId(data.org_id);
        setIsDetailsFetched(true);
      }),
    );

  //Submit ADONP refund
  const { mutate: submitForm, isLoading: isSubmittingForm } = useMutationCall(
    {
      onSuccess: (res) => {
        toast.success(res?.message ?? TEXT?.REFUND_SET_SUCCESS);
        reset();
        setIsDetailsFetched(false);
      },
    },
    ADONP_ENDPOINT,
    ['remove_delay_dialler'],
    {
      doNotShowSuccessToast: true,
    },
    (payload) => client(ADONP_ENDPOINT, { data: payload }),
  );

  // handle check credentials
  const handleCheckCredentials = () => {
    const userId = watch('userId');
    const cartId = watch('cartId');

    if (!userId) {
      setError('userId', {
        type: 'manual',
        message: ERRORS.USER_ID_MISSING,
      });
    }

    if (!cartId) {
      setError('cartId', {
        type: 'manual',
        message: ERRORS.CART_ID_MISSING,
      });
    }

    if (userId && cartId) {
      setError('userId', {
        type: 'manual',
        message: null,
      });
      setError('cartId', {
        type: 'manual',
        message: null,
      });
    }

    if (userId && cartId) {
      getCartDetails();
    }
  };

  // handle form submission
  const onSubmit = (data) => {
    const payload = {
      org_id: orgId,
      cart_id: watch('cartId'),
      user_id: watch('userId'),
      txn_id: data?.txnId,
    };
    submitForm(payload);
  };

  // useEffect to reset error messages
  useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (name === 'userId' && values?.userId) {
        setError('userId', { type: 'manual', message: null });
      }
      if (name === 'cartId' && values?.cartId) {
        setError('cartId', { type: 'manual', message: null });
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setError]);

  return (
    <form
      className='tw-w-full tw-flex tw-flex-col tw-h-max tw-gap-10 tw-bg-white tw-p-8 tw-rounded-2xl'
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className='tw-flex tw-w-full tw-gap-5 tw-items-stretch tw-justify-between'>
        {/* User Id input field */}
        <div className='tw-flex tw-flex-col tw-h-max tw-gap-5 tw-flex-1'>
          <div className='tw-w-full tw-color-[#1F1F1F] tw-font-bold tw-text-500'>
            User ID <Star icon='*' />
          </div>
          <div className='tw-flex tw-flex-col tw-w-full'>
            <input
              id='userId'
              name='userId'
              type='text'
              placeholder='Enter the user id'
              className={`tw-appearance-none tw-px-5 tw-py-2 tw-h-16 tw-rounded-lg tw-outline-0  focus:tw-border-none focus:tw-ring-[1.5px] tw-border tw-border-solid tw-border-[#cfcfcf] focus:tw-ring-green-700 focus:tw-border-green-700`}
              autoComplete='off'
              disabled={isDetailsFetched}
              {...register('userId', { required: ERRORS.USER_ID_MISSING })}
            />
          </div>
          {errors?.userId?.message && (
            <div className='tw-text-red-500 tw-text-sm tw-leading-none'>
              {errors.userId.message}
            </div>
          )}
        </div>
        {/* Cart Id input field */}
        <div className='tw-flex tw-flex-col tw-h-max tw-gap-5 tw-flex-1'>
          <div className='tw-w-full tw-color-[#1F1F1F] tw-font-bold tw-text-500'>
            Cart ID <Star icon='*' />
          </div>
          <div className='tw-flex tw-flex-col tw-w-full'>
            <input
              id='cartId'
              name='cartId'
              type='text'
              placeholder='Enter the cart id'
              className={`tw-appearance-none tw-px-5 tw-py-2 tw-h-16 tw-rounded-lg tw-outline-0  focus:tw-border-none focus:tw-ring-[1.5px] tw-border tw-border-solid tw-border-[#cfcfcf] focus:tw-ring-green-700 focus:tw-border-green-700`}
              autoComplete='off'
              disabled={isDetailsFetched}
              {...register('cartId', { required: ERRORS.CART_ID_MISSING })}
            />
          </div>
          {errors?.cartId?.message && (
            <div className='tw-text-red-500 tw-text-sm tw-leading-none'>
              {errors.cartId.message}
            </div>
          )}
        </div>
        {/* Check for details button */}
        {!isDetailsFetched && (
          <div className='tw-flex tw-items-end'>
            <button
              onClick={handleCheckCredentials}
              className='tw-text-[1.4rem] tw-flex tw-items-center tw-justify-center tw-font-medium tw-bg-green-700 tw-text-white tw-rounded-[0.6rem] tw-border-none tw-px-6 tw-py-4 tw-cursor-pointer tw-w-40 tw-h-16'
              type='button'
            >
              {isFetchingCartDetails ? (
                <Spinner color='white' size='small' />
              ) : (
                'Check'
              )}
            </button>
          </div>
        )}
      </div>

      {isDetailsFetched && (
        <div className='tw-flex tw-w-full tw-h-max tw-gap-5 tw-items-end'>
          {/* Title input field */}
          <div className='tw-flex tw-flex-col tw-h-max tw-gap-5 tw-flex-1'>
            <div className='tw-w-full tw-color-[#1F1F1F] tw-font-bold tw-text-500'>
              Title
            </div>
            <div className='tw-flex tw-flex-col tw-w-full'>
              <input
                id='title'
                name='title'
                type='text'
                placeholder='Enter the title'
                disabled
                className={`tw-appearance-none tw-px-5 tw-py-2 tw-h-16 tw-rounded-lg tw-outline-0  focus:tw-border-none focus:tw-ring-[1.5px] tw-w-full tw-border tw-border-solid tw-border-[#cfcfcf] tw-mr-6`}
                autoComplete='off'
                {...register('title')}
              />
            </div>
          </div>
          {/* Transaction Id input field */}
          <div className='tw-flex tw-flex-col tw-h-max tw-gap-5 tw-flex-1'>
            <div className='tw-w-full tw-color-[#1F1F1F] tw-font-bold tw-text-500'>
              Transaction ID
            </div>
            <div className='tw-flex tw-flex-col tw-w-full'>
              <input
                id='txnId'
                name='txnId'
                type='text'
                placeholder='Enter the Transaction id'
                disabled
                className={`tw-appearance-none tw-px-5 tw-py-2 tw-h-16 tw-rounded-lg tw-outline-0  focus:tw-border-none focus:tw-ring-[1.5px] tw-w-full tw-border tw-border-solid tw-border-[#cfcfcf] tw-mr-6`}
                autoComplete='off'
                {...register('txnId')}
              />
            </div>
          </div>
          {/* Cart Id input field */}
          <div className='tw-flex tw-flex-col tw-h-max tw-gap-5 tw-flex-1'>
            <div className='tw-w-full tw-color-[#1F1F1F] tw-font-bold tw-text-500'>
              Amount
            </div>
            <div className='tw-flex tw-flex-col tw-w-full'>
              <input
                id='amount'
                name='amount'
                type='text'
                placeholder='Enter the amount'
                disabled
                className={`tw-appearance-none tw-px-5 tw-py-2 tw-h-16 tw-rounded-lg tw-outline-0  focus:tw-border-none focus:tw-ring-[1.5px] tw-w-full tw-border tw-border-solid tw-border-[#cfcfcf] tw-mr-6`}
                autoComplete='off'
                {...register('amount')}
              />
            </div>
          </div>
        </div>
      )}

      {isDetailsFetched && (
        <div className='tw-flex tw-w-full tw-h-max tw-gap-5 tw-items-end'>
          {/* Organization input field */}
          <div className='tw-flex tw-flex-col tw-h-max tw-gap-5 tw-flex-1'>
            <div className='tw-w-full tw-color-[#1F1F1F] tw-font-bold tw-text-500'>
              Organization
            </div>
            <div className='tw-flex tw-flex-col tw-w-full'>
              <input
                id='org'
                name='org'
                type='text'
                placeholder='Enter the Organization'
                disabled
                className={`tw-appearance-none tw-px-5 tw-py-2 tw-h-16 tw-rounded-lg tw-outline-0  focus:tw-border-none focus:tw-ring-[1.5px] tw-w-full tw-border tw-border-solid tw-border-[#cfcfcf] tw-mr-6`}
                autoComplete='off'
                {...register('org')}
              />
            </div>
          </div>
          {/* Initiated Timestamp input field */}
          <div className='tw-flex tw-flex-col tw-h-max tw-gap-5 tw-flex-1'>
            <div className='tw-w-full tw-color-[#1F1F1F] tw-font-bold tw-text-500'>
              Initiated Timestamp
            </div>
            <div className='tw-flex tw-flex-col tw-w-full'>
              <input
                id='initiatedTs'
                name='initiatedTs'
                type='text'
                placeholder='Enter the Initiated Timestamp'
                disabled
                className={`tw-appearance-none tw-px-5 tw-py-2 tw-h-16 tw-rounded-lg tw-outline-0  focus:tw-border-none focus:tw-ring-[1.5px] tw-w-full tw-border tw-border-solid tw-border-[#cfcfcf] tw-mr-6`}
                autoComplete='off'
                {...register('initiatedTs')}
              />
            </div>
          </div>
          {/* Mode input field */}
          <div className='tw-flex tw-flex-col tw-h-max tw-gap-5 tw-flex-1'>
            <div className='tw-w-full tw-color-[#1F1F1F] tw-font-bold tw-text-500'>
              Mode
            </div>
            <div className='tw-flex tw-flex-col tw-w-full'>
              <input
                id='mode'
                name='mode'
                type='text'
                placeholder='Enter the Mode'
                disabled
                className={`tw-appearance-none tw-px-5 tw-py-2 tw-h-16 tw-rounded-lg tw-outline-0  focus:tw-border-none focus:tw-ring-[1.5px] tw-w-full tw-border tw-border-solid tw-border-[#cfcfcf] tw-mr-6`}
                autoComplete='off'
                {...register('mode')}
              />
            </div>
          </div>
        </div>
      )}

      {isDetailsFetched && (
        <div className='tw-flex tw-items-center tw-gap-5 tw-justify-end'>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            type='button'
            onClick={() => {
              reset();
              setIsDetailsFetched(false);
            }}
            className='tw-text-[1.4rem] tw-font-medium tw-bg-none tw-text-green-700 tw-rounded-[0.6rem] tw-border-none tw-px-6 tw-py-4 tw-cursor-pointer tw-w-40 tw-h-16 tw-flex tw-items-center tw-justify-center hover:tw-bg-grey-100 tw-duration-200 tw-transition-all tw-ease-in-out'
          >
            Reset
          </div>
          <button
            type='submit'
            className='tw-text-[1.4rem] tw-font-medium tw-bg-green-700 tw-text-white tw-rounded-[0.6rem] tw-border-none tw-px-6 tw-py-4 tw-cursor-pointer tw-w-40 tw-h-16 tw-flex tw-items-center tw-justify-center'
          >
            {isSubmittingForm ? (
              <Spinner color='white' size='small' />
            ) : (
              'Submit'
            )}
          </button>
        </div>
      )}
    </form>
  );
};

export default AdonpDashboard;
